import {useHistoryAssetsByIdQuery, useHistoryAssetsQuery} from '@domain/assets'
import {CommentsCategory} from '@domain/comments'
import {useUpmPlantTimezone, useUpmPlantContext} from '@hconnect/upm'
import {Dialog, DialogContent, DialogTitle} from '@mui/material'
import {Moment} from 'moment-timezone'
import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'

import {PLANNING_COMMENTS_WIDGET_NAMESPACE} from '../consts'
import {useAssetCommentsQuery, useCommentsPermissions} from '../hooks'
import {useSelectedAssetId} from '../providers'

import {AssetCommentForm} from './AssetCommentForm'

interface AddEditAssetCommentDialogProps {
  startOfPlan: Moment
  endOfPlan: Moment
  areCommentsEnabled: boolean
}

export const AddEditAssetCommentDialog: FC<AddEditAssetCommentDialogProps> = ({
  startOfPlan,
  endOfPlan,
  areCommentsEnabled
}) => {
  const {t} = useTranslation(PLANNING_COMMENTS_WIDGET_NAMESPACE)
  const timezoneId = useUpmPlantTimezone()
  const {canViewComments, canEditComments, canAddComments} = useCommentsPermissions(
    CommentsCategory.Assets
  )
  const {assetId, setAssetId} = useSelectedAssetId()
  const {plantId} = useUpmPlantContext()
  const {data: assets} = useHistoryAssetsQuery({
    plantId,
    timeFrame: [startOfPlan, endOfPlan],
    sorted: true
  })
  const {data: assetsById} = useHistoryAssetsByIdQuery({
    plantId,
    timeFrame: [startOfPlan, endOfPlan]
  })
  const isAssetCommentsQueryDisabled = !areCommentsEnabled || !canViewComments
  const {data: comments} = useAssetCommentsQuery(
    assets?.map((asset) => asset.id),
    isAssetCommentsQueryDisabled
  )

  if (assetId === undefined || !areCommentsEnabled || !(canAddComments && canEditComments)) {
    return null
  }

  const assetComment = comments?.find(
    ({commentsCategoryInstanceId: entityInstanceId}) => entityInstanceId === assetId.toString()
  )

  return (
    <Dialog open={true} onClose={() => setAssetId(undefined)} fullWidth>
      <DialogTitle>
        {t('planning.assetComment', {assetName: assetsById?.[assetId].name ?? t('common.loading')})}
      </DialogTitle>
      <DialogContent>
        <AssetCommentForm timezoneId={timezoneId} comment={assetComment} assetId={assetId} />
      </DialogContent>
    </Dialog>
  )
}
